import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Quote } from "lucide-react";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";

export default function AboutPage() {
  const p = useSection("page_about", {});
  const settings = useSettings({ square_booking_url: "" });
  const ctaUrl = p.cta_url || settings.square_booking_url;
  const lessons = Array.isArray(p.lessons) ? p.lessons.filter(Boolean) : [];

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/about" image={p.hero_image_url} />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} subheading={p.hero_subheading} image={p.hero_image_url} />

      {/* Intro — image + body */}
      <section className="bg-[#0a0a0a] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-x-0 gap-y-10 md:gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            {p.intro_image_url && (
              <div className="bento-card overflow-hidden aspect-[4/5] relative">
                <img
                  src={resolveUrl(p.intro_image_url)}
                  alt="Built Different Hockey on-ice training session"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover grayscale contrast-125"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">
                    {p.intro_label || "The Game"}
                  </div>
                  <div className="font-display text-2xl md:text-3xl uppercase leading-[0.95]">
                    {p.intro_heading || "Why hockey shapes who you become."}
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          <div className="md:col-span-7">
            <p className="overline">{p.intro_label || "The Game"}</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-4 leading-tight">
              {p.intro_heading || "Why hockey shapes who you become."}
            </h2>
            <div className="mt-7 text-zinc-300 leading-relaxed text-base md:text-lg whitespace-pre-line">
              {p.intro_body}
            </div>
          </div>
        </div>
      </section>

      {/* Lessons — bullets */}
      {lessons.length > 0 && (
        <section className="bg-[#0f0f0f] py-20 md:py-24 border-b border-white/5">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16">
            <p className="overline">{p.lessons_label || "What the Game Taught Me"}</p>
            <h2 className="font-display text-3xl md:text-5xl uppercase mt-4 leading-tight mb-10 max-w-3xl">
              {p.lessons_heading || "Preparation. Accountability. Consistency."}
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              {lessons.map((l, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="flex items-start gap-3 border-b border-white/10 pb-4 text-zinc-200"
                >
                  <CheckCircle2 className="h-5 w-5 text-brand-light shrink-0 mt-1" />
                  <span className="text-base md:text-lg">{l}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Military — text + image (image right) */}
      <section className="bg-[#0a0a0a] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-x-0 gap-y-10 md:gap-12 items-start">
          <div className="md:col-span-7 md:order-1 order-2">
            <p className="overline">{p.military_label || "Why Military Service"}</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-4 leading-tight">
              {p.military_heading || "A promise. A purpose."}
            </h2>
            <div className="mt-7 text-zinc-300 leading-relaxed text-base md:text-lg whitespace-pre-line">
              {p.military_body}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 md:order-2 order-1"
          >
            {p.military_image_url && (
              <div className="bento-card overflow-hidden aspect-[4/5] relative">
                <img
                  src={resolveUrl(p.military_image_url)}
                  alt="Coach Mike Fournier — 20-year United States military veteran"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover grayscale contrast-125"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">
                    Service · Discipline
                  </div>
                  <div className="font-display text-2xl uppercase leading-[0.95]">
                    Trust. Professionalism. Team.
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* What 'Built Different' Means — pull-quote feel on navy band */}
      <section className="bg-brand py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
          <div className="flex items-start gap-4">
            <Quote className="h-8 w-8 md:h-10 md:w-10 text-brand-light shrink-0 -mt-2" />
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">
                {p.definition_label || "What 'Built Different' Means"}
              </p>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-4 leading-[0.95]">
                {p.definition_heading || "Not gimmicks. Not shortcuts. The work, repeated."}
              </h2>
              <div className="mt-8 text-white/90 leading-relaxed text-base md:text-lg whitespace-pre-line max-w-3xl">
                {p.definition_body}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development standard */}
      <section className="bg-[#0f0f0f] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
          <p className="overline">{p.development_label || "The BDH Standard"}</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-4 leading-tight max-w-3xl">
            {p.development_heading || "Development. Habits. Hockey IQ. Character."}
          </h2>
          <div className="mt-7 text-zinc-300 leading-relaxed text-base md:text-lg whitespace-pre-line max-w-3xl">
            {p.development_body}
          </div>
        </div>
      </section>

      {/* Closing + CTA */}
      <section className="bg-[#0a0a0a] py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
          <p className="overline">{p.closing_label || "The Goal"}</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-4 leading-tight max-w-3xl">
            {p.closing_heading || "Help players become better hockey players, better teammates, and more resilient people."}
          </h2>
          <p className="mt-7 text-zinc-300 leading-relaxed text-base md:text-lg whitespace-pre-line max-w-3xl">
            {p.closing_body}
          </p>
          {ctaUrl && (
            <div className="mt-10">
              <a
                href={ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="about-cta-book"
              >
                {p.cta_label || "Book a Clinic"} <ArrowUpRight className="h-5 w-5" />
              </a>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
